import { Link } from "react-router-dom";

export default function NoIncomeTaxStatesPost() {
    return (
        <>
            <p>
                Florida. Texas. Tennessee. Nevada. Every few months a friend
                forwards me a Zillow listing in one of these states and asks
                some version of the same question: <em>&ldquo;If I move there,
                I save 5–10% of my income, right?&rdquo;</em> The marketing
                certainly suggests yes. The math, almost always, says it&apos;s
                more complicated than that. This piece is the honest answer.
            </p>

            <h2>The nine states with no personal income tax</h2>
            <p>
                As of 2025, nine US states levy no personal income tax on
                wages: <strong>Alaska, Florida, Nevada, New Hampshire, South
                Dakota, Tennessee, Texas, Washington, and Wyoming</strong>.
                (New Hampshire historically taxed interest and dividends; that
                tax is being phased out by 2027. Washington has a 7% capital
                gains surtax above $262K, but no wage tax.)
            </p>
            <p>
                On its face, this is real money. A single filer earning $85,000
                in California pays roughly $4,800 in state income tax. The same
                filer in Texas pays $0. Annualized over a 30-year career, the
                gap looks enormous.
            </p>

            <h2>But states still need revenue — they just collect it differently</h2>
            <p>
                State governments fund schools, roads, hospitals, and police
                from <em>somewhere</em>. The nine no-income-tax states recoup
                via three main levers:
            </p>
            <ul>
                <li><strong>Property tax.</strong> Texas, New Hampshire, and Wyoming have
                    some of the highest effective property tax rates in the country
                    (1.7%–2.0% of home value annually).</li>
                <li><strong>Sales tax.</strong> Tennessee&apos;s combined state + local
                    sales tax averages 9.55% — the highest in the US. Washington runs
                    around 9.4%. Texas ~8.2%.</li>
                <li><strong>Other levies.</strong> Florida leans on tourism and homeowners
                    insurance (which is high). Alaska runs on oil severance taxes.</li>
            </ul>
            <p>
                Whether the trade-off favors you depends almost entirely on
                what you spend money on, where you live <em>within</em> the
                state, and your home value relative to income.
            </p>

            <h2>Worked example: $100K earner, Austin TX vs San Diego CA</h2>
            <p>
                Let&apos;s run the numbers on a $100,000 single filer buying a
                $400K home in each market:
            </p>
            <ul>
                <li><strong>San Diego, CA:</strong> $0 state income tax savings vs.
                    California. State income tax on $100K single ≈ $5,800. Property tax
                    on $400K home @ 0.74% effective ≈ $2,960/year.</li>
                <li><strong>Austin, TX:</strong> No state income tax. Property tax on
                    $400K home @ 1.85% effective ≈ $7,400/year.</li>
            </ul>
            <p>
                California: $5,800 + $2,960 = <strong>$8,760</strong>/year in state + property tax. <br />
                Texas: $0 + $7,400 = <strong>$7,400</strong>/year. <br />
                <em>Texas wins by about $1,360/year.</em>
            </p>
            <p>
                That margin is real but smaller than most people imagine. And
                if you bought a $700K home in Austin instead of $400K? Texas
                property tax would be ~$13,000/year, and California suddenly
                wins. (Use our{" "}
                <Link to="/calculator/income-tax/tx">Texas income tax calculator</Link>{" "}
                and{" "}
                <Link to="/calculator/income-tax/ca">California income tax calculator</Link>{" "}
                to plug in your actual numbers.)
            </p>

            <h2>When &ldquo;no income tax&rdquo; actually wins</h2>
            <p>You&apos;re a strong winner if:</p>
            <ul>
                <li>You earn well above your state median (income-tax brackets are progressive — the savings scale with you).</li>
                <li>You rent rather than own (sidestep the property-tax catch).</li>
                <li>You&apos;re self-employed or a remote worker who can choose your domicile.</li>
                <li>You make most of your income from capital gains (most no-income-tax states don&apos;t tax those either; Washington is the exception above $262K).</li>
                <li>You retire there with a paid-off house and don&apos;t care about state-funded schools.</li>
            </ul>

            <h2>When it&apos;s a wash (or worse)</h2>
            <ul>
                <li>You buy an expensive primary home — property tax wipes the savings.</li>
                <li>You&apos;re a moderate earner in a state with low effective income tax already (Indiana 3.05%, Pennsylvania 3.07%).</li>
                <li>You spend most of your income on taxable goods (sales tax adds up fast).</li>
                <li>You have school-aged kids and end up paying private tuition because school quality is uneven.</li>
            </ul>

            <h2>The honest answer</h2>
            <p>
                Moving for taxes is rarely <em>just</em> about taxes. People
                move for jobs, family, weather, cost of housing, and lifestyle —
                and the tax math sweetens or sours an otherwise lifestyle-driven
                decision. If you&apos;re a high earner with a flexible job and
                you&apos;d be happy in a no-income-tax state for non-tax reasons
                anyway, the math will likely come out in your favor by
                $5K–$25K/year. If you&apos;re moving <em>only</em> for the tax
                savings? Run our state calculators on your actual income, your
                actual home price, and your actual spending — then look at the
                gap. Often it&apos;s thinner than the headline suggests.
            </p>

            <h2>Tools to run your own numbers</h2>
            <ul>
                <li><Link to="/calculator/income-tax">Federal income tax calculator</Link> — start here for a baseline</li>
                <li><Link to="/calculator/take-home/tx">Texas take-home pay</Link> · <Link to="/calculator/take-home/fl">Florida</Link> · <Link to="/calculator/take-home/wa">Washington</Link></li>
                <li><Link to="/calculator/take-home/ca">California take-home pay</Link> · <Link to="/calculator/take-home/ny">New York</Link> · <Link to="/calculator/take-home/nj">New Jersey</Link></li>
                <li><Link to="/calculator/mortgage/tx">Texas mortgage calculator</Link> — plug in your home price + property tax</li>
                <li><Link to="/calculator/rent-vs-buy">Rent vs Buy calculator</Link> — for the housing-decision side</li>
            </ul>

            <p className="text-sm text-muted-foreground italic mt-8">
                State and federal tax laws change every year. The numbers above
                use 2025 rates and should be re-checked against your actual
                state revenue department before making a relocation decision.
                When in doubt, consult a CPA in both your current and target state.
            </p>
        </>
    );
}
